import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Route, Redirect, withRouter } from "react-router-dom";
import { Layout, Spin } from "antd";
import { STATE_STATUS, ROUTE_PERMISSIONS, DEFERRAL_RESTRICTED_ROUTES } from "constants";
import { hasRestrictedGroupForDeferralMenu } from "constants/user-visibility";

/**
 * Permission-aware <Route>.
 *
 * Resolves the required permission from the explicit `permission` prop, falling
 * back to the central ROUTE_PERMISSIONS map keyed by `path`. Behaviour:
 *   - no permission required        -> renders the route normally
 *   - permissions still loading     -> shows a spinner (so a hard refresh on a
 *                                       guarded URL does not bounce the user out
 *                                       before permissions arrive)
 *   - permission present            -> renders the route
 *   - permission missing            -> redirects to /app/inbox
 *
 * Forwards `path`/`exact`/`strict` via `...rest`, so it still works inside a
 * <Switch> (Switch matches on the child element's `path` prop).
 */
const ProtectedRoute = ({
    user,
    permissions,
    permissionsStatus,
    permission,
    component: Component,
    render,
    ...rest
}) => {
    const requiredPermission = permission || ROUTE_PERMISSIONS[rest.path];

    // Some routes are additionally blocked for users in restricted groups, mirroring
    // the header menu (e.g. db-onboarding / anchor-onboarding for deferral groups).
    const isDeferralRestricted =
        DEFERRAL_RESTRICTED_ROUTES.includes(rest.path) &&
        hasRestrictedGroupForDeferralMenu(user);

    const renderContent = props => {
        if (Component) return <Component {...props} />;
        if (render) return render(props);
        return null;
    };

    // Open route: nothing to guard.
    if (!requiredPermission) {
        return <Route {...rest} render={renderContent} />;
    }

    return (
        <Route
            {...rest}
            render={props => {
                if (permissionsStatus !== STATE_STATUS.FETCHED) {
                    return (
                        <Layout className={"vh-100 justify-content-center"}>
                            <Spin />
                        </Layout>
                    );
                }
                if (
                    permissions &&
                    permissions.get(requiredPermission) &&
                    !isDeferralRestricted
                ) {
                    return renderContent(props);
                }
                return <Redirect to={"/app/inbox"} />;
            }}
        />
    );
};

ProtectedRoute.propTypes = {
    user: PropTypes.object,
    permissions: PropTypes.object,
    permissionsStatus: PropTypes.string,
    permission: PropTypes.string,
    path: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    component: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    render: PropTypes.func
};

const mapStateToProps = state => ({
    user: state.user,
    permissions: state.user.get("permissions"),
    permissionsStatus: state.user.get("permissionsStatus")
});

// withRouter must wrap connect() so a location change re-renders the component.
// Without it, react-redux's connect() blocks updates on navigation (props are
// unchanged), and the inner <Route> never re-matches the new URL.
export default withRouter(connect(mapStateToProps)(ProtectedRoute));
